import {Suspense, useRef} from 'react'
import {ActionList} from '@primer/react'
import {clsx} from 'clsx'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {SidebarMergeWhenReadyButton} from './SidebarMergeWhenReadyButton'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {MergeStatusRow} from './MergeStatusRow'
import {useMergeStatusRows} from './use-merge-status-rows'
import {useValidatedMergeData} from './use-validated-merge-data'
import {SidebarSection} from '@github-ui/pull-requests/components/overview/SidebarSection'
import {SidebarDisclosureHeader} from '@github-ui/pull-requests/components/overview/SidebarDisclosureHeader'
import {
  DockedPanel,
  useDockedPanelActions,
  useIsDockedPanelOpen,
} from '@github-ui/pull-requests/contexts/DockedPanelContext'
import sidebarListStyles from '@github-ui/pull-requests/components/overview/SidebarActionList.module.css'
import {SidebarFaviconSync} from './SidebarFaviconSync'
import {MergeStatusSkeleton} from './MergeStatusSkeleton'

export function OverviewMergeStatus() {
  return (
    <ErrorBoundary boundaryName="OverviewMergeStatus" fallback={null}>
      <OverviewMergeStatusGate />
    </ErrorBoundary>
  )
}

function OverviewMergeStatusGate() {
  const validatedMergeData = useValidatedMergeData()

  // `validatedMergeData` comes from the SSR-available layout route data, so this gate
  // resolves on the server. Only mount the Suspense boundary (and its skeleton) when the
  // section will actually have content, so closed/opted-out PRs don't render a placeholder
  // that would disappear on the client.
  if (!validatedMergeData) return null

  return (
    <Suspense fallback={<MergeStatusSkeleton />}>
      <OverviewMergeStatusContent />
    </Suspense>
  )
}

function OverviewMergeStatusContent() {
  const rows = useMergeStatusRows()
  const {togglePanel, openPanel} = useDockedPanelActions()
  const isPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)
  const headerRef = useRef<HTMLButtonElement>(null)
  if (!rows) return null

  const header = (
    <SidebarDisclosureHeader
      ref={headerRef}
      title="Merge status"
      trailingIcon={ArrowUpRightIcon}
      aria-expanded={isPanelOpen}
      aria-label={isPanelOpen ? 'Close merge status panel' : 'Open merge status panel'}
      onClick={() => togglePanel(DockedPanel.MergeStatus, {returnFocusTo: headerRef.current})}
    />
  )

  return (
    <SidebarSection title="Merge status" header={header}>
      <ErrorBoundary boundaryName="SidebarFaviconSync" fallback={null}>
        <SidebarFaviconSync />
      </ErrorBoundary>

      <div className="d-flex flex-column gap-1">
        {rows.length === 0 ? (
          <p className="mb-0 color-fg-muted text-small">No merge requirements currently blocking this pull request.</p>
        ) : (
          <ActionList variant="full" className={clsx(sidebarListStyles.flush, sidebarListStyles.compact)}>
            {rows.map(row => (
              <MergeStatusRow
                key={`${row.label}-${row.detail ?? ''}`}
                row={row}
                onSelect={returnFocusTo => openPanel(DockedPanel.MergeStatus, {returnFocusTo})}
              />
            ))}
          </ActionList>
        )}
        <SidebarMergeWhenReadyButton />
      </div>
    </SidebarSection>
  )
}
